import { z } from 'zod';
import { PillowDesignKeySchema } from './pillow-design.schema';

// ═══════════════════════════════════════════════════
//  Pillow design catalog rows
// ═══════════════════════════════════════════════════
//
// One row of the pillow "Design" dropdown, as served by
// GET /api/v1/pillow-designs.
//
// This schema is the type source, not a runtime gate: nothing calls .parse() on
// PillowDesignOptionSchema, and the client hook casts the response via
// apiClient.get<PillowDesignOption[]>. The runtime fallback for an unknown key
// lives in resolvePillowDesignTraits.

export const PillowDesignOptionSchema = z.object({
  id: z.string().uuid(),
  key: PillowDesignKeySchema,
  name: z.string().min(1),
  sort_order: z.number().int(),
  active: z.boolean(),
  /** A welt (piping) runs the perimeter and is charged per linear foot. */
  has_welt: z.boolean(),
  /** A cylinder: the welt becomes two end-cap circumferences, not a perimeter. */
  is_bolster: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type PillowDesignOption = z.infer<typeof PillowDesignOptionSchema>;

// ═══════════════════════════════════════════════════
//  Write payloads
// ═══════════════════════════════════════════════════

/** Trait fields, shared by create and update. */
const PillowTraitFields = {
  has_welt: z.boolean().default(false),
  is_bolster: z.boolean().default(false),
};

/**
 * `key` is optional on create: the server derives it from `name` when omitted,
 * which is what the UI does. Supplying it explicitly is allowed so a caller can
 * pin an exact key.
 *
 * `sort_order` is not accepted at all — a new design always lands at max + 1.
 */
export const CreatePillowDesignSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  key: PillowDesignKeySchema.optional(),
  active: z.boolean().default(true),
  ...PillowTraitFields,
});

/**
 * No `key`. It is stored inside every saved decorative_pillow_config and is a
 * top-level key in pl_design_type_overrides, so changing it would orphan them.
 * .strict() so a UI bug that tries to rename a key 400s instead of being
 * silently absorbed.
 */
export const UpdatePillowDesignSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    sort_order: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
    has_welt: z.boolean().optional(),
    is_bolster: z.boolean().optional(),
  })
  .strict();

export type CreatePillowDesign = z.infer<typeof CreatePillowDesignSchema>;
export type UpdatePillowDesign = z.infer<typeof UpdatePillowDesignSchema>;
